import { CommonModule } from '@angular/common';
import { Component, EventEmitter, forwardRef, Input, OnInit, Output, SimpleChange, SimpleChanges } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { FormControlValueAccessor } from '../../accessors';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'eap-input',
  imports: [
    CommonModule,
    MatIconModule,
    ReactiveFormsModule,
    // custom components
    ButtonComponent,
    NgxMaskDirective
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputComponent),
      multi: true
    },
    provideNgxMask(),
  ],

  templateUrl: './input.component.html',
  styleUrl: './input.component.scss'
})
export class InputComponent extends FormControlValueAccessor<string> implements OnInit {
  //#region Inputs
  @Input() label = '';                      // Label shown above input
  @Input() placeholder = '';                // Placeholder text inside input
  @Input() error = false;                   // External error flag (optional)
  @Input() assistiveText = '';              // Inline hint or error message
  @Input() assistiveIcon?: string;          // Optional icon with assistive text
  @Input() type: 'text' | 'password' | 'number' | 'text-area' = 'text';  // Input type
  @Input() showButton = false;              // Show icon button inside input (e.g., eye icon)
  @Input() buttonIcon = 'visibility';       // Material icon shown in the button
  @Input() disabled = false;                // Allow disabling the both input & button externally
  @Input() isInputReadOnly = false;         // Allow disabling only the input externally
  @Input() transformUpperCase = false;      // Allow Uppercase in the input
  @Input() keypressFn: Function = () => { };
  @Input() inputMask: string | undefined;
  @Input() unBindMaskedValue: boolean = false;
  @Input() maxLength: number | null = null;
  @Input() inputClass: string = "";
  @Input() id: string = '';
  @Input() allowToShowPlaceHolder: boolean = false;
  @Input() showTextButton: boolean = false;
  @Input() textButtonLabel: string = '';
  @Input() textButtonClass: string | string[] = '';
  @Input() isPassword: Boolean = false
  @Input() showLargeIcon: Boolean = false
  @Input() inputValue: string = '';
  @Input() showMaskValidationMessage: boolean = false;
  @Input() showSearchIcon: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() isSearchGrey: boolean = true;
  @Input() isTimeMasking: boolean = false;
  @Input() inputMaskValidation!: boolean;
  @Input() textTransform!: 'uppercase' | 'lowercase' | 'capitalize' | 'unset';
  @Input() textAlign!: 'left' | 'right';
  @Input() textAreaResize!:  'none' | 'both' | 'horizontal' | 'vertical' | 'block' | 'inline';
  @Input() textAreaRow: string = '4'
  @Input() labelOffset?: string; // consumer can override
  //#endregion

  //#region Outputs
  @Output() blurEvent = new EventEmitter<void>();      // Emits on input blur
  @Output() focusEvent = new EventEmitter<void>();      // Emits on input focus
  @Output() inputEvent = new EventEmitter<string>();   // Emits on input change
  @Output() inputWithEvent = new EventEmitter<Event>();   // Emits on input change
  @Output() buttonClickEvent = new EventEmitter<void>();   // Emits on button click
  @Output() changeEvent = new EventEmitter<void>();   // Emits on button click
  @Output() keydownEvent = new EventEmitter<KeyboardEvent>();   // Emits on keydowb in input
  //#endregion

  isFocused = false;    // Tracks input focus state
  showPassword = false; // For toggling password visibility
  emptyPatterns!: Record<string, { pattern: RegExp; optional?: boolean; symbol?: string }>;
  public timePatterns = {
    H: { pattern: /[0-2]/ },     // first hour digit
    h: { pattern: /[0-9]/ },     // second hour digit
    m: { pattern: /[0-5]/ },     // first minute digit
    0: { pattern: /[0-9]/ },     // second minute digit
    X: { pattern: /[AaPp]/ },    // A or P
    M: { pattern: /[Mm]/ },      // M
  };

  ngOnInit(): void {
    this.control.valueChanges.subscribe((value) => {
      this.onChange(value);
    })
    // Subscribe to control status to auto-detect errors
    this.control.statusChanges.subscribe(() => {
      this.setControlState();
    });
  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes["disabled"]) {

      if (changes["disabled"].currentValue) {
        this.control.disable();
      } else {
        this.control.enable();
      }
    }

    if (changes["inputValue"]) {

      if (changes["inputValue"].currentValue) {
        this.control.setValue(changes["inputValue"].currentValue);
      } else {
        this.control.setValue('');
      }
    }
  }

  /**
   * Determine input type: toggle password/text if applicable
   */
  computedType(): string {
    return this.type === 'password' && this.showPassword ? 'text' : this.type;
  }

  /**
   * Toggle input type visibility (e.g., password <-> text)
   */
  onButtonClick(): void {
    if (this.type == 'password') {
      this.showPassword = !this.showPassword;
      this.buttonIcon = this.showPassword ? 'visibility_off' : 'visibility';
    } else {
      this.buttonClickEvent.emit();
    }

  }

  /**
   * Emits blur event and triggers touched handler
   */
  onBlur(): void {
    this.onModelTouched();
    this.isFocused = false;
    this.blurEvent.emit();
  }

  onFocus(): void {
    this.isFocused = true;
    this.focusEvent.emit();
  }

  onInputChange() {
    this.changeEvent.emit();
  }
  
  onInputKeydown(event: KeyboardEvent) {
    this.keydownEvent.emit(event);
  }

  /**
   * Evaluates the control’s validation state and updates UI error flag/message
   */
  private setControlState(): void {
    if (this.control.invalid && (this.control.dirty || this.control.touched)) {
      this.error = true;

      const errorKey = Object.keys(this.control.errors!)[0];

      const defaultMessage = (errorKey == 'mask' && !this.showMaskValidationMessage) ? '' : 'Invalid value';

      if (errorKey == 'mask') {
        this.validate = () => {
          return this.control.errors;
        };
      }

      const defaultMessages: Record<string, string> = {
        required: 'This field is required.',
        minlength: `Minimum ${this.control.errors?.['minlength']?.requiredLength} characters required.`,
        maxlength: `Maximum ${this.control.errors?.['maxlength']?.requiredLength} characters allowed.`,
        pattern: 'Invalid format.',
      };

      this.assistiveText = defaultMessages[errorKey] || defaultMessage;
      this.assistiveIcon = 'error';
    } else {
      this.error = false;
      this.assistiveText = '';
      this.assistiveIcon = '';
      this.validate = () => {
        return null;
      }
    }
  }


  get isDisabled() {
    return this.disabled;
  }

}
